import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { consumePendingWidgetAction } from '../utils/widget';

// Routes widget + Siri actions to the matching in-app action. Two sources:
//
//   1. Widget tile taps arrive as `com.decisivelabsllc.fueltron://widget/<action>`
//      deep links via Capacitor's `appUrlOpen` event.
//   2. Siri "open the app" App Intents (iOS 16/17 can't use iOS 18's
//      OpenURLIntent) stash an action in the App Group; we pick it up on
//      launch + resume via consumePendingWidgetAction().
//
// Supported actions:
//   camera   — open the scan camera flow
//   voice    — open AI bar in voice mode
//   exercise — open exercise log
//   score    — open the dashboard
//
// Camera/voice piggyback on the existing `openAIAssistant` CustomEvent
// pattern (see PersistentAIBar.tsx) so we don't duplicate the action
// plumbing. Score/exercise are plain route navigations.
//
// Mounted once at the top of the authenticated app (inside Router), so
// the navigate handle is valid.

/**
 * iOS hands back the SAME launch URL for the whole life of the process, so it
 * must only ever be acted on once. Module scope (not a ref) because the hook's
 * host can remount — e.g. logging out and back in — and a remount must not
 * re-fire the launch action. Genuine later taps arrive as `appUrlOpen` events.
 */
let launchUrlHandled = false;

export function useWidgetDeepLink(): void {
  const navigate = useNavigate();

  // react-router memoises `navigate` on the current pathname, so its identity
  // changes on EVERY navigation. Depending on it here re-ran the whole effect
  // after each route change, which re-read the launch URL and bounced the user
  // straight back to the widget's screen — you could never reach the dashboard
  // after opening the app from the lock-screen camera tile. Keep it in a ref and
  // run the effect exactly once.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const routeAction = (action: string | null | undefined) => {
      if (!action) return;
      const go = navigateRef.current;
      switch (action) {
        case 'camera':
          go('/scan', { state: { action: 'camera' } });
          break;
        case 'voice':
          go('/dashboard');
          // PersistentAIBar listens for this and opens the chat in voice mode.
          window.dispatchEvent(
            new CustomEvent('openAIAssistant', { detail: { action: 'voice' } })
          );
          break;
        case 'exercise':
          go('/exercise-log');
          break;
        case 'score':
        default:
          go('/dashboard');
          break;
      }
    };

    const handleUrl = (url: string) => {
      let parsed: URL;
      try {
        parsed = new URL(url);
      } catch {
        return;
      }

      // Match anything ending /widget/<action> — be tolerant of host
      // placement across `host/path` parsing edge cases.
      const segments = [parsed.host, ...parsed.pathname.split('/')].filter(Boolean);
      const widgetIdx = segments.indexOf('widget');
      if (widgetIdx === -1) return;
      routeAction(segments[widgetIdx + 1]);
    };

    // Drain any Siri pending action stashed in the App Group. This one is
    // self-clearing, so repeat calls are safe.
    const drainPending = () => {
      consumePendingWidgetAction().then(routeAction).catch(() => {});
    };

    let cleanup: (() => void) | null = null;

    Promise.all([
      // Every tile tap while the app is already running arrives here — always act.
      CapApp.addListener('appUrlOpen', (event) => {
        if (event?.url) handleUrl(event.url);
      }),
      // App returns to foreground (e.g. after Siri launched it) — check for
      // a freshly-stashed pending action.
      CapApp.addListener('resume', drainPending),
    ])
      .then((listeners) => {
        cleanup = () => listeners.forEach((l) => l.remove());
      })
      .catch(() => {
        // Capacitor unavailable — no-op
      });

    // Honor a launch URL that arrived before the listener attached (cold start
    // from a widget tile). Once only — see `launchUrlHandled` above.
    if (!launchUrlHandled) {
      launchUrlHandled = true;
      CapApp.getLaunchUrl()
        .then((res) => {
          if (res?.url) handleUrl(res.url);
        })
        .catch(() => {});
    }

    // And a Siri action that opened the app from cold start.
    drainPending();

    return () => {
      if (cleanup) cleanup();
    };
    // Intentionally empty: this wires process-lifetime listeners exactly once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
